import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  GlCheckData,
  GLConfigCategory,
  GLConfiguration,
  GlDivision,
  GlExportHistory,
  GlMappingGroup,
  GlQboAccount,
  GlTotalsData,
  QboCredentialRequirements
} from '../models/general-ledger';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PagedPayload } from '../models/paged-payload';

@Injectable({
  providedIn: 'root'
})
export class GeneralLedgerService {
  constructor(
    private http: HttpClient,
    @Inject('env') private env: any,
    @Inject('errorInterceptorSkipHeader') private errorInterceptorSkipHeader: any
  ) {}

  public createGLConfiguration(clientCode: string, config: GLConfiguration) {
    return this.http.post<GLConfiguration>(this._clientUrl(clientCode), config);
  }

  public updateGlConfiguration(clientCode: string, config: GLConfiguration) {
    return this.http.put(`${this._clientUrl(clientCode)}/${config.Id}`, config);
  }

  public createGlMappingGroups(clientCode: string, configId: number, group: GlMappingGroup[]) {
    return this.http.post<GLConfiguration>(
      `${this._clientUrl(clientCode)}/${configId}/Groups`,
      group
    );
  }
  public getTotalsData(clientCode: string, configId: number) {
    return this.http.get<GlTotalsData[]>(
      `${this._clientUrl(clientCode)}/${configId}/ExportTotalsData`
    );
  }
  public getChecksData(clientCode: string, configId: number) {
    return this.http.get<GlCheckData[]>(
      `${this._clientUrl(clientCode)}/${configId}/ExportCheckData`
    );
  }

  public getQboAccounts(clientCode: string, configId: number) {
    const headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');
    return this.http.get<GlQboAccount[]>(`${this._clientUrl(clientCode)}/${configId}/QboAccounts`, {
      headers
    });
  }
  public getQboClasses(clientCode: string, configId: number) {
    const headers = new HttpHeaders().set(this.errorInterceptorSkipHeader, '');
    return this.http.get<GlQboAccount[]>(`${this._clientUrl(clientCode)}/${configId}/QboClasses`, {
      headers
    });
  }
  public updateGlMappingGroup(
    clientCode: string,
    configId: number,
    groupId: number,
    group: GlMappingGroup
  ) {
    return this.http.put<GLConfiguration>(
      `${this._clientUrl(clientCode)}/${configId}/Groups/${groupId}`,
      group
    );
  }

  public getGlMappingGroups(clientCode: string, configId: number) {
    return this.http.get<GlMappingGroup[]>(`${this._clientUrl(clientCode)}/${configId}/Groups`);
  }
  public completeQboConnection(clientCode: string, credential: QboCredentialRequirements) {
    return this.http.post(`${this._clientUrl(clientCode)}/QboConnect`, credential);
  }

  public getGLConfigurations(clientCode: string): Observable<GLConfiguration[]> {
    return this.http.get<GLConfiguration[]>(`${this._clientUrl(clientCode)}`);
  }

  public getGLConfigCategories(clientCode: string): Observable<GLConfigCategory[]> {
    return this.http
      .get<GLConfigCategory[]>(`${this.env.apiUrlRoot}/Companies/${clientCode}/GLConfigCategories`)
      .pipe(
        map(categories => {
          const final: GLConfigCategory[] = [];
          const parents = categories.filter(x => x.Parent == null);
          parents.forEach(parent => {
            parent.Children = categories.filter(x => x.Parent == parent.Code);
            final.push(parent);
          });
          return final;
        })
      );
  }

  public getGlCheckCategories(clientCode: string): Observable<GlMappingGroup> {
    return this.http.get<GlMappingGroup>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/GLCheckCategories`
    );
  }

  public getGlDivisions(clientCode: string): Observable<GlDivision[]> {
    return this.http.get<GlDivision[]>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/GlConfigColumns`
    );
  }

  public getGlExportHistory(
    clientCode: string,
    pageNumber: number = 1,
    perPage: number = 25
  ): Observable<PagedPayload<GlExportHistory>> {
    let params = new HttpParams();
    params = params.set('page_number', pageNumber);
    params = params.set('per_page', perPage);
    return this.http.get<PagedPayload<GlExportHistory>>(
      `${this.env.apiUrlRoot}/Companies/${clientCode}/GlDownloadHistory`,
      { params }
    );
  }

  public deleteGlConfig(clientCode: string, configId: number) {
    return this.http.delete(`${this._clientUrl(clientCode)}/${configId}`);
  }
  public duplicateGlConfig(clientCode: string, configId: number) {
    return this.http.post(`${this._clientUrl(clientCode)}/${configId}/duplicate`, {});
  }

  public exportToCsv(clientCode: string, configId: number) {
    return this.http.get(`${this._clientUrl(clientCode)}/${configId}/ExportToCSV`, {
      responseType: 'blob' as 'json'
    });
  }

  public exportToQbDesktop(clientCode: string, configId: number) {
    return this.http.get(`${this._clientUrl(clientCode)}/${configId}/ExportToIIF`, {
      responseType: 'blob' as 'json'
    });
  }
  public exportToQbo(clientCode: string, configId: number) {
    return this.http.get(`${this._clientUrl(clientCode)}/${configId}/ExportToQbo`);
  }
  private _clientUrl(clientId: string) {
    return `${this.env.apiUrlRoot}/Companies/${clientId}/GLConfigurations`;
  }
}
